/** 16-step (sixteenth-note) rhythm patterns. 'x' = hit, 'X' = accent, anything else = rest. */
import type { Rng } from '../util';

export type Pattern = Uint8Array;

export function pat(s: string): Pattern {
  const p = new Uint8Array(16);
  for (let i = 0; i < 16 && i < s.length; i++) {
    const c = s[i];
    p[i] = c === 'X' ? 2 : c === 'x' ? 1 : 0;
  }
  return p;
}

export function pats(list: readonly string[]): readonly Pattern[] {
  return list.map(pat);
}

export const EMPTY: Pattern = pat('');

export function lastHit(p: Pattern): number {
  for (let i = 15; i >= 0; i--) if (p[i]) return i;
  return -1;
}

/** Melody rhythm cells, grouped by density. Phrases breathe: plenty of space, few busy bars. */
export const MELODY_SPARSE = pats([
  'x...............',
  '........x.......',
  'x.......x.......',
  'x.....x.........',
  '....x...........',
  'x...........x...',
]);

export const MELODY_MEDIUM = pats([
  'x.....x...x.....',
  'x...x...x.......',
  '..x...x...x.....',
  'x.....x.....x...',
  'x.x.....x...x...',
  'x...x.x.....x...',
  'x.......x.x.x...',
]);

export const MELODY_DENSE = pats([
  'x.x.x...x.x.x...',
  'x..x..x.x..x..x.',
  'x.x.x.x.x...x...',
  '..x.x.x...x.x.x.',
  'x..x..x...x.x.x.',
  'x.x...x.x.x.x...',
]);

/** Phrase-ending bars: land on a long, resolved note. */
export const MELODY_CADENCE = pats(['x.......x.......', 'x...x...x.......', 'x...............', 'x.....x.x.......']);

/** Pick a rhythm cell for the given density (0..1) with a little randomness. */
export function chooseRhythm(rng: Rng, density: number): Pattern {
  const d = density + rng.bi() * 0.15;
  if (d < 0.28) return rng.pick(MELODY_SPARSE);
  if (d < 0.58) return rng.pick(MELODY_MEDIUM);
  return rng.pick(MELODY_DENSE);
}
